import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Inbox, MapPin, Users, Clock, Phone, Mail, Lock, CheckCircle2, Star, XCircle, PhoneCall, Calendar } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { agencyMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LoadingScreen, EmptyState } from "@/components/common";

const RESP = [
  { key: "ACCEPTED", label: "Accept", icon: CheckCircle2, cls: "bg-emerald-600 hover:bg-emerald-700 text-white" },
  { key: "INTERESTED", label: "Interested", icon: Star, cls: "bg-brand hover:bg-brand-600 text-white" },
  { key: "CONTACTED", label: "Contact Customer", icon: PhoneCall, cls: "border-navy-300", variant: "outline" },
  { key: "NOT_AVAILABLE", label: "Not Available", icon: Clock, cls: "border-navy-300 text-navy-500", variant: "outline" },
  { key: "REJECTED", label: "Reject", icon: XCircle, cls: "border-red-200 text-red-600 hover:bg-red-50", variant: "outline" },
];
const RESP_PILL = { PENDING: "bg-sky-100 text-sky-700", ACCEPTED: "bg-emerald-100 text-emerald-700", INTERESTED: "bg-brand-100 text-brand-600", CONTACTED: "bg-violet-100 text-violet-700", NOT_AVAILABLE: "bg-amber-100 text-amber-700", REJECTED: "bg-red-100 text-red-700" };

export default function AgencyMyLeads() {
  const [rows, setRows] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/agency/my-leads").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const respond = async (asnId, response) => {
    try { await api.post(`/agency/my-leads/${asnId}/respond`, { response }); toast.success("Response recorded"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const openDetail = async (asnId) => {
    try { const r = await api.get(`/agency/my-leads/${asnId}`); setDetail(r.data); load(); }
    catch { toast.error("Could not load lead"); }
  };

  return (
    <DashboardShell menu={agencyMenu} title="My Leads">
      <PageHeader title="My Leads" subtitle="Customer requirements forwarded to your agency by the GuardDekho team." />
      {!rows ? <LoadingScreen /> : rows.length === 0 ? (
        <EmptyState icon={Inbox} title="No forwarded leads yet" hint="When the GuardDekho team forwards a customer requirement to your agency, it will appear here." />
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="my-leads-list">
          {rows.map(({ assignment: a, lead: l }) => (
            <div key={a.id} className="card-lift bg-white border border-navy-200 rounded-xl p-5" data-testid={`myleads-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="brand-label text-brand">{a.lead_no}</p>
                  <h3 className="font-heading font-semibold text-navy-900">{l.security_requirement}</h3>
                  <p className="text-sm text-navy-500">{l.company_name || "Individual"} · {l.property_type}</p>
                </div>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${RESP_PILL[a.response] || RESP_PILL.PENDING}`}>{a.response === "PENDING" ? "New" : a.response.replace(/_/g, " ")}</span>
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-navy-600">
                <span className="flex items-center gap-1"><MapPin size={14} /> {l.location}</span>
                <span className="flex items-center gap-1"><Users size={14} /> {l.num_guards} guards</span>
                <span className="flex items-center gap-1"><Clock size={14} /> {l.shift_duration}h × {l.num_shifts}</span>
                <span className="flex items-center gap-1 text-navy-400"><Calendar size={13} /> {a.created_at?.slice(0, 10)}</span>
              </div>
              <div className="mt-3 rounded-lg bg-navy-50 border border-navy-100 px-3 py-2.5 text-sm space-y-1">
                <p className="text-navy-800 flex items-center gap-2"><Users size={13} className="text-brand" /> {l.customer_name}</p>
                {l.email && <p className="text-navy-700 flex items-center gap-2"><Mail size={13} /> {l.email}</p>}
                {l.contact_unlocked
                  ? <a href={`tel:${l.mobile}`} className="text-navy-800 flex items-center gap-2 hover:text-brand"><Phone size={13} /> {l.mobile}</a>
                  : <p className="text-navy-500 flex items-center gap-2" data-testid={`myleads-phone-locked-${a.id}`}><Lock size={13} className="text-brand" /> Phone shared once admin authorises</p>}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="border-navy-300" onClick={() => openDetail(a.id)} data-testid={`myleads-view-${a.id}`}>View</Button>
                {RESP.map((b) => (
                  <Button key={b.key} size="sm" variant={b.variant} className={b.cls}
                    onClick={() => respond(a.id, b.key)} data-testid={`myleads-${b.key.toLowerCase()}-${a.id}`}>
                    <b.icon size={14} className="mr-1" /> {b.label}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="myleads-detail">
          <DialogHeader><DialogTitle>{detail?.assignment?.lead_no} — {detail?.lead?.security_requirement}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3 text-navy-700">
                <div><span className="text-navy-400 text-xs block">Location</span>{detail.lead.location} {detail.lead.pincode}</div>
                <div><span className="text-navy-400 text-xs block">Guards</span>{detail.lead.num_guards}</div>
                <div><span className="text-navy-400 text-xs block">Duty</span>{detail.lead.shift_duration}h × {detail.lead.num_shifts} shift(s)</div>
                <div><span className="text-navy-400 text-xs block">Duration</span>{detail.lead.contract_duration_months} months</div>
                <div><span className="text-navy-400 text-xs block">Start date</span>{detail.lead.start_date || "—"}</div>
                <div><span className="text-navy-400 text-xs block">Budget</span>{detail.lead.budget_range || "—"}</div>
              </div>
              <div className="rounded-lg bg-navy-50 border border-navy-100 p-3">
                <p className="text-navy-800 font-medium">{detail.lead.customer_name}</p>
                {detail.lead.email && <p className="text-navy-600">{detail.lead.email}</p>}
                <p className={detail.lead.contact_unlocked ? "text-navy-800" : "text-navy-500"}>{detail.lead.contact_unlocked ? detail.lead.mobile : "Phone shared once admin authorises"}</p>
              </div>
              {detail.lead.special_instructions && <div><span className="text-navy-400 text-xs block">Instructions</span><p className="text-navy-700">{detail.lead.special_instructions}</p></div>}
              {detail.assignment.message && <div className="rounded-lg bg-brand-50 border border-brand-100 p-3 text-navy-700"><span className="brand-label text-brand block mb-1">Message from GuardDekho</span>{detail.assignment.message}</div>}
              <div className="flex flex-wrap gap-2 pt-1">
                {RESP.map((b) => (
                  <Button key={b.key} size="sm" variant={b.variant} className={b.cls} onClick={() => { respond(detail.assignment.id, b.key); setDetail(null); }}>
                    <b.icon size={14} className="mr-1" /> {b.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
